import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MonoLabel } from "../components/MonoLabel";
import { ToastStack, useToasts } from "../components/Toast";
import {
  OkoshiSessionManager,
  type OkoshiState,
  type OkoshiStopResult,
} from "../channels/okoshi/OkoshiSessionManager";
import {
  buildMarkdownForDownload,
  buildMarkdownForVault,
  nextOkoshiPathPreview,
} from "../channels/okoshi/format";
import {
  formatDuration,
  readLastSession,
  writeLastSession,
  type OkoshiLastSession,
} from "../channels/okoshi/lastSession";
import {
  type OkoshiTranscribeModel,
  type OkoshiVaultMode,
  type Settings,
  type VaultSaveResponse,
} from "../lib/api-types";
import type { SelectedDevices } from "../lib/deviceStorage";

/** Periodic updater tick, used for elapsed + nextPath preview. */
const TICK_MS = 1000;

/** UI-level phase on top of the session-manager state. `stopped` holds the
 *  action panel after STOP resolves until the user closes it or auto-save
 *  returns to idle. */
type UiMode = "idle" | "starting" | "recording" | "stopping" | "stopped";

/**
 * Koe Okoshi tab — continuous transcription channel.
 * spec §1.3, §2.3.2, §2.10.
 */
export function OkoshiTab({
  settings,
  onSettingsChanged,
  devices,
}: {
  settings: Settings;
  /** Reserved for future in-tab settings shortcuts. */
  onSettingsChanged?: (settings: Settings) => void;
  /** Selected audio devices from App-level state (spec §2.8). */
  devices?: SelectedDevices;
}) {
  // Silence unused-param for now; App passes this in for future use.
  void onSettingsChanged;

  // ── Session state mirrored from the manager ──────────────
  const [state, setState] = useState<OkoshiState>("idle");
  const [liveTranscript, setLiveTranscript] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // ── Post-stop state ──────────────────────────────────────
  const [mode, setMode] = useState<UiMode>("idle");
  const [stopResult, setStopResult] = useState<OkoshiStopResult | null>(null);

  // ── Ticking info ─────────────────────────────────────────
  const [elapsedSec, setElapsedSec] = useState(0);
  const [nextPath, setNextPath] = useState(() => nextOkoshiPathPreview());

  // ── Persisted "last session" footer (idle screen) ────────
  const [lastSession, setLastSession] = useState<OkoshiLastSession | null>(
    () => readLastSession(),
  );

  const managerRef = useRef<OkoshiSessionManager | null>(null);
  const sessionStartRef = useRef<Date | null>(null);
  const { toasts, push: pushToast, dismiss: dismissToast } = useToasts();
  const transcriptBoxRef = useRef<HTMLDivElement | null>(null);

  // ── 1Hz tick for elapsed time + next path preview ────────
  useEffect(() => {
    const tick = () => {
      setNextPath(nextOkoshiPathPreview());
      if (mode === "recording" && sessionStartRef.current) {
        setElapsedSec(
          Math.floor(
            (Date.now() - sessionStartRef.current.getTime()) / 1000,
          ),
        );
      }
    };
    const id = window.setInterval(tick, TICK_MS);
    return () => window.clearInterval(id);
  }, [mode]);

  // ── Auto-scroll transcript box on update ─────────────────
  useEffect(() => {
    const box = transcriptBoxRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [liveTranscript]);

  // ── Tear down manager on unmount ─────────────────────────
  useEffect(() => {
    return () => {
      managerRef.current?.stop().catch(() => {
        /* ignore — manager is being torn down */
      });
      managerRef.current = null;
    };
  }, []);

  // ── Save + persistence ───────────────────────────────────
  const persistLastSession = useCallback(
    (savedPath: string, durationSec: number, charCount: number) => {
      const entry: OkoshiLastSession = {
        savedPath,
        durationSec,
        charCount,
        savedAt: Date.now(),
      };
      writeLastSession(entry);
      setLastSession(entry);
    },
    [],
  );

  const saveToVault = useCallback(
    async (
      finalTranscript: string,
      startedAt: Date,
    ): Promise<VaultSaveResponse> => {
      // VaultRPC prepends the frontmatter block on the Vault MCP side, so we
      // send body-only markdown here to avoid the duplicate `---` issue.
      const { markdown, meta } = buildMarkdownForVault(
        finalTranscript,
        startedAt,
      );
      const res = await fetch("/api/vault/save", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          source: "Okoshi",
          title: meta.title,
          content: markdown,
          dateTimeIso: meta.dateTimeIso,
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `vault save failed: ${res.status} ${text.slice(0, 200)}`,
        );
      }
      const saved = (await res.json()) as VaultSaveResponse;
      if (stopResult) {
        persistLastSession(
          saved.savedPath,
          stopResult.durationSec,
          stopResult.charCount,
        );
      }
      return saved;
    },
    [persistLastSession, stopResult],
  );

  const runAutoSave = useCallback(
    async (result: OkoshiStopResult, startedAt: Date) => {
      try {
        const saved = await saveToVault(result.finalTranscript, startedAt);
        const body = result.hadPendingDelta
          ? `保存しました · 末尾は未確定のまま · ${saved.savedPath}`
          : `Vaultに保存しました · ${saved.savedPath}`;
        pushToast(body, {
          durationMs: 5000,
          ...(saved.vaultUrl
            ? { link: { href: saved.vaultUrl, label: "OPEN →" } }
            : {}),
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        pushToast(`ERR · VAULT_FAIL · ${msg}`, {
          tone: "error",
          durationMs: 5000,
        });
      }
    },
    [pushToast, saveToVault],
  );

  // ── Handlers: start / stop / actions ─────────────────────
  const handleStart = useCallback(async () => {
    if (managerRef.current) return;
    setErrorMessage(null);
    setStopResult(null);
    setLiveTranscript("");
    setElapsedSec(0);
    setMode("starting");

    const manager = new OkoshiSessionManager({
      onStateChange: setState,
      onTranscriptChange: () => {
        const m = managerRef.current;
        if (!m) return;
        setLiveTranscript(m.getLiveTranscript());
      },
      onSessionStarted: () => {
        sessionStartRef.current = new Date();
        setMode("recording");
      },
      onError: (m) => {
        setErrorMessage(m);
        pushToast(`ERR · ${m}`, "error");
      },
    });
    managerRef.current = manager;

    try {
      await manager.start(
        settings.okoshiTranscribeModel,
        devices?.inputId ?? null,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // Common case: user denied mic permission.
      if (
        msg.toLowerCase().includes("permission") ||
        msg.toLowerCase().includes("notallowed")
      ) {
        pushToast("ERR · NO_MIC · 0x07 — enable mic access", "error");
      } else {
        pushToast(`ERR · ${msg}`, "error");
      }
      setErrorMessage(msg);
      setMode("idle");
      managerRef.current = null;
    }
  }, [devices?.inputId, pushToast, settings.okoshiTranscribeModel]);

  const handleStop = useCallback(async () => {
    const manager = managerRef.current;
    if (!manager) return;
    setMode("stopping");
    let result: OkoshiStopResult;
    try {
      result = await manager.stop();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMessage(msg);
      pushToast(`ERR · ${msg}`, "error");
      setMode("idle");
      managerRef.current = null;
      return;
    }
    setLiveTranscript(manager.getLiveTranscript());
    setStopResult(result);
    managerRef.current = null;

    // Dispatch according to the configured vault policy.
    const startedAt = sessionStartRef.current ?? new Date();
    const policy: OkoshiVaultMode = settings.okoshiVaultMode;
    if (policy === "auto-save" && result.finalTranscript.trim().length > 0) {
      setMode("stopped");
      await runAutoSave(result, startedAt);
      setMode("idle");
    } else {
      setMode("stopped");
    }
  }, [pushToast, runAutoSave, settings.okoshiVaultMode]);

  const handleCopy = useCallback(async () => {
    const text = stopResult?.finalTranscript ?? "";
    try {
      await navigator.clipboard.writeText(text);
      pushToast("copied to clipboard");
    } catch {
      pushToast("ERR · copy failed", "error");
    }
  }, [pushToast, stopResult]);

  const handleDownload = useCallback(() => {
    if (!stopResult) return;
    const startedAt = sessionStartRef.current ?? new Date();
    const { markdown, meta } = buildMarkdownForDownload(
      stopResult.finalTranscript,
      startedAt,
    );
    const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `okoshi-${meta.stamp}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    pushToast(`downloaded · okoshi-${meta.stamp}.md`);
  }, [pushToast, stopResult]);

  const handleManualVaultSave = useCallback(async () => {
    if (!stopResult) return;
    const startedAt = sessionStartRef.current ?? new Date();
    try {
      const saved = await saveToVault(stopResult.finalTranscript, startedAt);
      const body = stopResult.hadPendingDelta
        ? `保存しました · 末尾は未確定のまま · ${saved.savedPath}`
        : `Vaultに保存しました · ${saved.savedPath}`;
      pushToast(body, {
        durationMs: 5000,
        ...(saved.vaultUrl
          ? { link: { href: saved.vaultUrl, label: "OPEN →" } }
          : {}),
      });
      setMode("idle");
      setStopResult(null);
      setLiveTranscript("");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      pushToast(`ERR · VAULT_FAIL · ${msg}`, {
        tone: "error",
        durationMs: 5000,
      });
    }
  }, [pushToast, saveToVault, stopResult]);

  const handleDismissResult = useCallback(() => {
    setMode("idle");
    setStopResult(null);
    setLiveTranscript("");
  }, []);

  // ── Derived ──────────────────────────────────────────────
  const elapsedLabel = useMemo(() => {
    const m = Math.floor(elapsedSec / 60);
    const s = elapsedSec % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, [elapsedSec]);

  // ── Render ───────────────────────────────────────────────
  return (
    <section className="flex min-h-full flex-col gap-5 px-5 py-6">
      <ToastStack toasts={toasts} onDismiss={dismissToast} />

      <div className="space-y-1">
        <div className="font-mono text-[10px] tracking-[0.22em] text-subtext">
          OKO · {modeLabel(mode, state)}
        </div>
        <h1 className="koe-hand text-4xl text-ink">
          {mode === "recording" ? "rolling." : "tape ready."}
        </h1>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <MonoLabel label="MODEL">
          {shortModelLabel(settings.okoshiTranscribeModel)}
        </MonoLabel>
        <MonoLabel label="POLICY">{settings.okoshiVaultMode}</MonoLabel>
      </div>

      {errorMessage && (
        <div
          role="alert"
          className="rounded-box border border-rust bg-cream-soft px-3 py-2 font-mono text-[11px] text-ink"
        >
          <span className="text-rust">ERR · </span>
          {errorMessage}
        </div>
      )}

      <div className="koe-rule-dotted" />

      {/* Idle — last session footer + next path preview */}
      {mode === "idle" && (
        <div className="space-y-2 font-mono text-[11px] text-subtext">
          <div>
            <span className="text-ink/70">LAST · </span>
            {lastSession ? (
              <span>
                {lastSession.savedPath}{" "}
                <span className="text-rust">✓</span>{" "}
                {formatDuration(lastSession.durationSec)} ·{" "}
                {lastSession.charCount} chars
              </span>
            ) : (
              <span className="italic opacity-70">no session yet</span>
            )}
          </div>
          <div>
            <span className="text-ink/70">NEXT · </span>
            {nextPath}
          </div>
        </div>
      )}

      {/* Recording — elapsed + live transcript */}
      {(mode === "recording" || mode === "stopping") && (
        <>
          <div className="flex items-baseline justify-between">
            <span className="font-mono text-[10px] tracking-[0.22em] text-subtext">
              ELAPSED
            </span>
            <span className="font-mono text-3xl tabular-nums tracking-tight text-ink">
              {elapsedLabel}
            </span>
          </div>

          <div
            ref={transcriptBoxRef}
            className="min-h-[200px] flex-1 overflow-y-auto rounded-box border border-ink/15 bg-cream-soft p-3 font-mono text-[13px] leading-relaxed text-ink"
          >
            {liveTranscript ? (
              <TranscriptBody
                text={liveTranscript}
                pendingDelta={managerRef.current?.getCurrentDelta() ?? ""}
              />
            ) : (
              <span className="text-subtext italic">
                listening · speak now
              </span>
            )}
          </div>

          {mode === "stopping" && (
            <p className="font-mono text-[10px] tracking-[0.18em] text-subtext">
              stopping · settling transcript…
            </p>
          )}
        </>
      )}

      {/* Stopped — action panel */}
      {mode === "stopped" && stopResult && (
        <div className="flex flex-col gap-3">
          <div className="flex items-baseline justify-between font-mono text-[11px] text-subtext">
            <span>
              {formatDuration(stopResult.durationSec)} · {stopResult.charCount}{" "}
              chars · {stopResult.segmentCount} segments
            </span>
            {stopResult.hadPendingDelta && (
              <span className="text-rust">末尾未確定</span>
            )}
          </div>
          <div className="max-h-[40vh] overflow-y-auto rounded-box border border-ink/15 bg-cream-soft p-3 font-mono text-[13px] leading-relaxed whitespace-pre-wrap text-ink">
            {stopResult.finalTranscript || (
              <span className="italic opacity-60">(空)</span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <ActionButton onClick={handleCopy}>COPY</ActionButton>
            <ActionButton onClick={handleDownload}>DL · .md</ActionButton>
            {settings.okoshiVaultMode !== "never" && (
              <ActionButton onClick={handleManualVaultSave}>
                VAULT
              </ActionButton>
            )}
          </div>
          <button
            type="button"
            onClick={handleDismissResult}
            className="self-end font-mono text-[10px] tracking-[0.2em] text-subtext hover:text-rust"
          >
            ✕ DISCARD &amp; BACK
          </button>
        </div>
      )}

      {/* REC / STOP button */}
      <div className="flex flex-1 flex-col items-center justify-end gap-3 pt-4 pb-2">
        {(mode === "idle" || mode === "stopped") && (
          <button
            type="button"
            onClick={handleStart}
            disabled={mode === "stopped"}
            aria-label="Start recording"
            className="flex h-32 w-32 select-none items-center justify-center rounded-full bg-rust font-mono text-[12px] tracking-[0.22em] text-cream shadow-md transition-transform active:scale-[0.98] hover:bg-rust/90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            REC
          </button>
        )}
        {mode === "starting" && (
          <button
            type="button"
            disabled
            className="flex h-32 w-32 items-center justify-center rounded-full bg-rust/40 font-mono text-[11px] tracking-[0.22em] text-cream"
          >
            LINK…
          </button>
        )}
        {(mode === "recording" || mode === "stopping") && (
          <button
            type="button"
            onClick={handleStop}
            disabled={mode === "stopping"}
            aria-label="Stop recording"
            className="flex h-32 w-32 items-center justify-center rounded-sm bg-ink font-mono text-[12px] tracking-[0.22em] text-cream shadow-md transition-transform active:scale-[0.98] disabled:opacity-60"
          >
            {mode === "stopping" ? "STOPPING…" : "STOP"}
          </button>
        )}
        <p className="font-mono text-[10px] tracking-[0.18em] text-subtext">
          {mode === "idle" && "tap to start · review before save"}
          {mode === "starting" && "opening channel…"}
          {mode === "recording" && "recording continuously"}
          {mode === "stopping" && "finalising last segment"}
          {mode === "stopped" && "review · save · or discard"}
        </p>
      </div>
    </section>
  );
}

function ActionButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-box border border-ink/30 px-3 py-2 font-mono text-[11px] tracking-[0.2em] text-ink hover:bg-ink hover:text-cream"
    >
      {children}
    </button>
  );
}

function TranscriptBody({
  text,
  pendingDelta,
}: {
  text: string;
  pendingDelta: string;
}) {
  // If pendingDelta is non-empty, the text ends with it (spec of
  // getLiveTranscript). Render it in a muted style so the user can tell
  // confirmed segments from the live tail.
  if (!pendingDelta) {
    return <span className="whitespace-pre-wrap">{text}</span>;
  }
  const head = text.endsWith(pendingDelta)
    ? text.slice(0, text.length - pendingDelta.length)
    : text;
  return (
    <>
      <span className="whitespace-pre-wrap">{head}</span>
      <span className="whitespace-pre-wrap opacity-60">{pendingDelta}</span>
    </>
  );
}

function modeLabel(mode: UiMode, state: OkoshiState): string {
  switch (mode) {
    case "idle":
      return state === "error" ? "FAULT" : "STBY";
    case "starting":
      return "LINK…";
    case "recording":
      return "REC";
    case "stopping":
      return "SETTLE";
    case "stopped":
      return "DONE";
  }
}

function shortModelLabel(model: OkoshiTranscribeModel): string {
  switch (model) {
    case "gpt-4o-mini-transcribe":
      return "4o-mini";
    case "gpt-4o-transcribe":
      return "4o";
    case "gpt-realtime-whisper":
      return "rt-whisper";
  }
}
